import Phaser from 'phaser';
import { ScoreService } from '../services/ScoreService';

/**
 * GameOverScene: Displays final score, high score comparison,
 * and provides options to play again or enter initials for high score.
 * All text uses neon glow effects for consistent contrast and visual style.
 */
export class GameOverScene extends Phaser.Scene {
  private finalScore = 0;
  private finalWave = 1;
  private isTransitioning = false;

  constructor() {
    super({ key: 'GameOverScene' });
  }

  init(data: { score: number; wave: number }): void {
    this.finalScore = data.score || 0;
    this.finalWave = data.wave || 1;
    this.isTransitioning = false;
  }

  /**
   * Safely transition to another scene, preventing duplicate transitions
   * from rapid clicks or key presses.
   */
  private safeSceneStart(sceneKey: string, data?: object): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.scene.start(sceneKey, data);
  }

  create(): void {
    const { width, height } = this.cameras.main;
    const scoreService = ScoreService.getInstance();

    // Game Over title with red neon glow
    this.add.text(width / 2, height * 0.2, 'GAME OVER', {
      fontSize: '36px',
      color: '#ff3333',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#ff3333', blur: 15, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Final score with white glow
    this.add.text(width / 2, height * 0.35, `SCORE: ${this.finalScore}`, {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#aaaaff', blur: 8, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Wave reached with cyan glow
    this.add.text(width / 2, height * 0.43, `WAVE: ${this.finalWave}`, {
      fontSize: '14px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#00ffff', blur: 6, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Check if this is a new high score
    const isHighScore = scoreService.isHighScore(this.finalScore);

    // Build menu items list dynamically based on whether high score was achieved
    const gameOverMenuItems: { text: Phaser.GameObjects.Text; action: () => void; defaultColor: string; defaultGlowColor: string }[] = [];

    if (isHighScore) {
      this.add.text(width / 2, height * 0.53, 'NEW HIGH SCORE!', {
        fontSize: '16px',
        color: '#ffff00',
        fontFamily: '"Press Start 2P", monospace',
        shadow: {
          offsetX: 0, offsetY: 0, color: '#ffff00', blur: 10, fill: true, stroke: true,
        },
      }).setOrigin(0.5, 0.5);

      // Go to high score entry with green glow
      const enterBtn = this.add.text(width / 2, height * 0.63, 'ENTER INITIALS', {
        fontSize: '16px',
        color: '#00ff41',
        fontFamily: '"Press Start 2P", monospace',
        shadow: {
          offsetX: 0, offsetY: 0, color: '#00ff41', blur: 8, fill: true, stroke: true,
        },
      });
      enterBtn.setOrigin(0.5, 0.5);
      enterBtn.setInteractive({ useHandCursor: true });
      enterBtn.on('pointerdown', () => {
        this.safeSceneStart('HighScoreEntryScene', {
          score: this.finalScore,
          wave: this.finalWave,
        });
      });
      gameOverMenuItems.push({
        text: enterBtn,
        action: () => this.safeSceneStart('HighScoreEntryScene', { score: this.finalScore, wave: this.finalWave }),
        defaultColor: '#00ff41',
        defaultGlowColor: '#00ff41',
      });

      // Auto-transition to entry after a delay
      this.time.delayedCall(5000, () => {
        if (this.scene.isActive()) {
          this.safeSceneStart('HighScoreEntryScene', {
            score: this.finalScore,
            wave: this.finalWave,
          });
        }
      });
    } else {
      // Show current high score
      const highScores = scoreService.getHighScores();
      if (highScores.length > 0) {
        this.add.text(width / 2, height * 0.53, `HIGH SCORE: ${highScores[0].score}`, {
          fontSize: '14px',
          color: '#00ffff',
          fontFamily: '"Press Start 2P", monospace',
          shadow: {
            offsetX: 0, offsetY: 0, color: '#00ffff', blur: 6, fill: true, stroke: true,
          },
        }).setOrigin(0.5, 0.5);
      }
    }

    // Play Again button with glow
    const playAgainBtn = this.add.text(width / 2, height * 0.78, 'PLAY AGAIN', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#ffffff', blur: 8, fill: true, stroke: true,
      },
    });
    playAgainBtn.setOrigin(0.5, 0.5);
    playAgainBtn.setInteractive({ useHandCursor: true });
    playAgainBtn.on('pointerdown', () => this.safeSceneStart('GameScene'));
    gameOverMenuItems.push({
      text: playAgainBtn,
      action: () => this.safeSceneStart('GameScene'),
      defaultColor: '#ffffff',
      defaultGlowColor: '#ffffff',
    });

    // High Scores button with green glow
    const highScoreBtn = this.add.text(width / 2, height * 0.88, 'HIGH SCORES', {
      fontSize: '14px',
      color: '#00ff41',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#00ff41', blur: 6, fill: true, stroke: true,
      },
    });
    highScoreBtn.setOrigin(0.5, 0.5);
    highScoreBtn.setInteractive({ useHandCursor: true });
    highScoreBtn.on('pointerdown', () => this.safeSceneStart('HighScoreScene'));
    gameOverMenuItems.push({
      text: highScoreBtn,
      action: () => this.safeSceneStart('HighScoreScene'),
      defaultColor: '#00ff41',
      defaultGlowColor: '#00ff41',
    });

    // Focus arrow indicator (▶)
    const focusArrow = this.add.text(0, 0, '▶', {
      fontSize: '14px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#00ffff', blur: 10, fill: true, stroke: true,
      },
    });
    focusArrow.setOrigin(0.5, 0.5);

    // Pulsing animation for focus arrow
    this.tweens.add({
      targets: focusArrow,
      alpha: { from: 0.6, to: 1.0 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Keyboard menu navigation
    let selectedGameOverItem = 0;

    const updateGameOverHighlight = () => {
      for (let i = 0; i < gameOverMenuItems.length; i++) {
        const item = gameOverMenuItems[i];
        if (i === selectedGameOverItem) {
          item.text.setColor('#00ffff');
          item.text.setShadow(0, 0, '#00ffff', 12, true, true);
        } else {
          item.text.setColor(item.defaultColor);
          item.text.setShadow(0, 0, item.defaultGlowColor, 8, true, true);
        }
      }
      // Position focus arrow
      const selectedText = gameOverMenuItems[selectedGameOverItem].text;
      const bounds = selectedText.getBounds();
      focusArrow.setPosition(bounds.x - 20, bounds.y + bounds.height / 2);
    };

    // Pointer hover updates selection
    gameOverMenuItems.forEach((item, index) => {
      item.text.on('pointerover', () => {
        selectedGameOverItem = index;
        updateGameOverHighlight();
      });
      item.text.on('pointerout', () => {
        updateGameOverHighlight();
      });
    });

    // Help text for keyboard navigation
    this.add.text(width / 2, height * 0.95, '↑↓ NAVIGATE  ENTER SELECT', {
      fontSize: '7px',
      color: '#8888aa',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#8888aa', blur: 4, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Keyboard shortcuts (delayed to prevent leftover key events from gameplay)
    this.time.delayedCall(500, () => {
      if (!this.scene.isActive()) return;
      this.input.keyboard?.on('keydown-UP', () => {
        selectedGameOverItem = (selectedGameOverItem - 1 + gameOverMenuItems.length) % gameOverMenuItems.length;
        updateGameOverHighlight();
      });
      this.input.keyboard?.on('keydown-DOWN', () => {
        selectedGameOverItem = (selectedGameOverItem + 1) % gameOverMenuItems.length;
        updateGameOverHighlight();
      });
      this.input.keyboard?.on('keydown-TAB', (event: KeyboardEvent) => {
        event.preventDefault();
        selectedGameOverItem = (selectedGameOverItem + 1) % gameOverMenuItems.length;
        updateGameOverHighlight();
      });
      this.input.keyboard?.on('keydown-SPACE', () => gameOverMenuItems[selectedGameOverItem].action());
      this.input.keyboard?.on('keydown-ENTER', () => gameOverMenuItems[selectedGameOverItem].action());
    });

    // Highlight first item (ENTER INITIALS if high score, otherwise PLAY AGAIN)
    updateGameOverHighlight();
  }

  /**
   * Clean up keyboard and input listeners when scene is shut down.
   * Prevents memory leaks from accumulated listeners across scene transitions.
   */
  shutdown(): void {
    this.input.keyboard?.removeAllListeners();
    this.input.removeAllListeners();
    // Kill all tweens (focus arrow pulse, etc.)
    this.tweens.killAll();
    // Remove all timer events (auto-transition delay, keyboard delay)
    this.time.removeAllEvents();
  }
}
